import {
  Button,
  FlexBox,
  Icon,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useFormContext } from 'react-hook-form';

import { OAuthFormData } from '../../../../oAuth.types';
import { useAuthentication } from '../../utils/useAuthentication/useAuthentication';

type FormActionsProps = {
  disabled: boolean;
  isSaving: boolean;
  hasFormValues: boolean;
  oAuthProviderId?: string;
  oAuthStrategy?: string;
  onSubmit: () => void;
};

/** The action steps for the OAuth form. */
export const FormActions = ({
  disabled,
  onSubmit,
  isSaving,
  hasFormValues,
  oAuthProviderId,
  oAuthStrategy,
}: FormActionsProps): JSX.Element => {
  const {
    formState: { isDirty },
  } = useFormContext<OAuthFormData>();

  const {
    connectionStatus,
    connectionStatusIcon: { icon, color, tooltip },
    handleAuthorization,
    resetConnectionStatus,
  } = useAuthentication({ oAuthProviderId, oAuthStrategy });

  const isConnecting = connectionStatus === 'CONNECTING';
  const enableAuthButton = disabled || (hasFormValues && !isDirty);

  return (
    <FlexBox flexDirection="row" gap="250" alignItems="end">
      <FlexBox flexDirection="column" gap="100" alignItems="center">
        <Text.Paragraph color={!disabled ? 'primary' : 'tertiary'}>
          Step 1
        </Text.Paragraph>

        <Button
          disabled={disabled}
          isLoading={isSaving}
          onClick={() => {
            resetConnectionStatus();
            onSubmit();
          }}
        >
          Save configuration
        </Button>
      </FlexBox>

      <FlexBox flexDirection="column" gap="100" alignItems="center">
        <Text.Paragraph color={enableAuthButton ? 'primary' : 'tertiary'}>
          Step 2
        </Text.Paragraph>

        <Tooltip
          content={
            !enableAuthButton
              ? 'The OAuth configuration needs to be saved before you can authorize it'
              : null
          }
        >
          <Button
            icon="key"
            disabled={!enableAuthButton}
            onClick={handleAuthorization}
          >
            Authorize
          </Button>
        </Tooltip>
      </FlexBox>

      {hasFormValues && (
        <Tooltip content={tooltip}>
          <FlexBox margin="0 0 200">
            {isConnecting ? (
              <Spinner data-testid="oauth-button-spinner" size={18} />
            ) : (
              <Icon name={icon} color={color} />
            )}
          </FlexBox>
        </Tooltip>
      )}
    </FlexBox>
  );
};
